'use client'

import { faAngleDown } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { NavigationMenu } from 'radix-ui'
import { cva } from 'class-variance-authority'
import {
  useTranslations,
  type AbstractIntlMessages,
  useMessages,
} from 'next-intl'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState, Fragment } from 'react'
import { cn } from 'utils/cn'
import { Indicator } from './Indicator'
import { Submenu } from './Submenu'
import { type MenuItem } from './typings'
import { isSamePath } from './utils'

const menuItem = cva(
  'hover:text-primary-700 hover:border-primary-700 data-[state=open]:text-primary-700 data-[state=open]:border-primary-700 active:text-primary-800 active:border-primary-800 group flex h-[theme(height.header-mobile)] flex-row items-center gap-1 whitespace-nowrap border-b-[3px] border-transparent pb-[3px] *:translate-y-[3px]',
  {
    variants: {
      active: {
        true: 'text-primary-700 border-primary-700',
      },
    },
  },
)

export const MainMenu = ({
  className,
  onMenuItemClick,
}: {
  className?: string
  onMenuItemClick?: (args: {
    clickedElement?: string
    clickUrl?: string
  }) => void
}) => {
  const [offset, setOffset] = useState<number | null>(null)
  const listRef = useRef<HTMLUListElement | null>(null)
  const [value, setValue] = useState('')
  const [activeTrigger, setActiveTrigger] = useState<HTMLButtonElement | null>(
    null,
  )

  const t = useTranslations('Navigation.MainMenu')
  const messages = useMessages() as AbstractIntlMessages & {
    Navigation?: {
      MainMenu?: {
        Items: Record<string, MenuItem>
      }
    }
  }
  const items = messages.Navigation?.MainMenu?.Items
  const path = usePathname()

  useEffect(
    function calculateContentOffset() {
      const list = listRef.current
      if (activeTrigger && list) {
        setOffset(
          Math.round(activeTrigger.offsetLeft - activeTrigger.clientWidth / 2),
        )
      } else if (value === '') {
        setOffset(null)
      }
    },
    [activeTrigger, value],
  )

  if (!items) {
    return null
  }

  return (
    <NavigationMenu.Root
      aria-label={t('ariaLabel')}
      className={cn(
        'font-manrope text-text-800 text-body-md-bold relative',
        className,
      )}
      onValueChange={(e) => setValue(e)}
      value={value}
    >
      <NavigationMenu.List
        className='flex flex-row items-center gap-8 lg:gap-4 xl:gap-8'
        ref={listRef}
      >
        {Object.entries(items).map(([_, item]) => {
          const triggerId = `nav-trigger-${item.title}`

          return (
            <Fragment key={item.title}>
              {!item.Children ? (
                <NavigationMenu.Item value={item.title}>
                  <NavigationMenu.Link asChild>
                    <Link
                      className={cn(
                        menuItem({
                          active: isSamePath(path, item.path ?? '/'),
                        }),
                      )}
                      href={item.path ?? '/'}
                      onClick={() =>
                        onMenuItemClick?.({
                          clickedElement: item.title,
                          clickUrl: item.path ?? '/',
                        })
                      }
                      prefetch={false}
                      title={item.title}
                      type='button'
                    >
                      <span>{item.title}</span>
                    </Link>
                  </NavigationMenu.Link>
                </NavigationMenu.Item>
              ) : (
                <NavigationMenu.Item value={item.title}>
                  <NavigationMenu.Trigger
                    className={cn(
                      menuItem({
                        active: isSamePath(path, item.path ?? '/'),
                      }),
                    )}
                    id={triggerId}
                    onPointerDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    ref={(node) => {
                      if (item.title === value && activeTrigger !== node) {
                        setActiveTrigger(node)
                      }
                    }}
                  >
                    <span>{item.title}</span>
                    <FontAwesomeIcon
                      className={cn(
                        'transition-transform duration-300 ease-in group-data-[state=open]:-rotate-180',
                      )}
                      icon={faAngleDown}
                    />
                  </NavigationMenu.Trigger>
                  <NavigationMenu.Content
                    aria-labelledby={triggerId}
                    className={cn(
                      'data-[motion=from-end]:animate-enterFromRight shadow-menu data-[motion=from-start]:animate-enterFromLeft data-[motion=to-end]:animate-exitToRight data-[motion=to-start]:animate-exitToLeft absolute w-max rounded-md transition-all duration-200',
                      {
                        'opacity-100': item.title === value,
                        '-z-10 opacity-0': item.title !== value,
                      },
                    )}
                    forceMount
                  >
                    <Submenu
                      menuItem={item}
                      onSubMenuItemClick={onMenuItemClick}
                    />
                  </NavigationMenu.Content>
                </NavigationMenu.Item>
              )}
            </Fragment>
          )
        })}
        <Indicator />
      </NavigationMenu.List>
      <div
        style={{
          display: !offset ? 'none' : undefined,
          left: offset ? offset : 0,
          position: 'absolute',
        }}
      >
        <NavigationMenu.Viewport
          className='@container/viewport bg-surface-primary shadow-menu data-[state=open]:animate-scaleIn data-[state=closed]:animate-scaleOut w-[var(--radix-navigation-menu-viewport-width] absolute mt-2.5 h-[var(--radix-navigation-menu-viewport-height)] w-full min-w-min origin-[top_center] overflow-hidden rounded-md transition-[width,height] duration-200 sm:w-[var(--radix-navigation-menu-viewport-width)]'
          style={{
            display: !offset ? 'none' : undefined,
          }}
        />
      </div>
    </NavigationMenu.Root>
  )
}
